import { useState, useEffect } from 'react'
import {
  Stack, Group, Text, Badge, Button, MultiSelect,
} from '@mantine/core'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { rolesApi, ROLE_COLORS, type Role } from '@/api/roles'
import { formatApiError } from '@/api/errors'
import { useAuthStore } from '@/store/auth'
import type { UserRow } from './UsersTable'

interface UserDrawerRolesProps {
  user: UserRow
}

export function UserDrawerRoles({ user }: UserDrawerRolesProps) {
  const { user: authUser, selectedTenantId, refreshUser } = useAuthStore()
  const ctxUserId = authUser?.id ?? 0
  const ctxTenantId = selectedTenantId ?? authUser?.tenants?.[0]?.id ?? 1
  const qc = useQueryClient()
  const isEditingSelf = authUser?.id === user.id

  const [pendingRoles, setPendingRoles] = useState<string[]>(user.roles ?? [])

  useEffect(() => {
    setPendingRoles(user.roles ?? [])
  }, [user.id, user.roles])

  const { data: allRoles = [] } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => rolesApi.listRoles(),
  })

  const roleOptions = allRoles.map((r) => ({ value: r.code, label: r.name || r.code }))

  const isDirty = (() => {
    const current = [...(user.roles ?? [])].sort()
    const pending = [...pendingRoles].sort()
    return current.length !== pending.length || current.some((r, i) => r !== pending[i])
  })()

  const saveRolesMutation = useMutation({
    mutationFn: async () => {
      const current = user.roles ?? []
      const toAdd = pendingRoles.filter((r) => !current.includes(r))
      const toRemove = current.filter((r) => !pendingRoles.includes(r))
      const actorEmail = authUser?.email ?? ''
      for (const r of toAdd) {
        await rolesApi.assignRole(user.id, r, actorEmail)
      }
      for (const r of toRemove) {
        try {
          await rolesApi.revokeRole(user.id, r)
        } catch (err: any) {
          if (err?.response?.status === 409) {
            const msg = formatApiError(err, 'Não é possível remover esta função.')
            notifications.show({
              color: 'orange',
              title: 'Ação bloqueada',
              message: msg,
              autoClose: 8000,
            })
            return
          }
          throw err
        }
      }
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      if (isEditingSelf) {
        await refreshUser()
      }
      notifications.show({ color: 'teal', title: 'Roles atualizados', message: 'Permissões salvas com sucesso.' })
    },
    onError: (err: any) => {
      const msg = formatApiError(err, 'Erro ao salvar roles.')
      notifications.show({ color: 'red', title: 'Erro', message: msg })
    },
  })

  const getRoleColor = (code: string): string => {
    const role = allRoles.find((r) => r.code === code)
    return role?.color || ROLE_COLORS[code] || 'gray'
  }

  return (
    <Stack gap="md" p="md">
      <Text size="xs" c="var(--ai-text-muted)" fw={500}>Funções atuais</Text>
      <Group gap={6}>
        {(user.roles ?? []).length === 0 && (
          <Text size="sm" c="dimmed">Nenhuma função atribuída</Text>
        )}
        {(user.roles ?? []).map((code) => (
          <Badge key={code} color={getRoleColor(code)} variant="light" size="sm">
            {allRoles.find((r) => r.code === code)?.name || code}
          </Badge>
        ))}
      </Group>

      <MultiSelect
        label="Funções atribuídas"
        description="Selecione as funções para este usuário"
        data={roleOptions}
        value={pendingRoles}
        onChange={setPendingRoles}
        searchable
        mt="sm"
      />

      <Group justify="flex-end" mt="sm">
        <Button
          leftSection={<IconDeviceFloppy size={14} />}
          loading={saveRolesMutation.isPending}
          disabled={!isDirty}
          onClick={() => saveRolesMutation.mutate()}
        >
          Salvar
        </Button>
      </Group>
    </Stack>
  )
}
